import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { api, ApiError } from '@/lib/api'
import type { EventInfo, Me, VerificationInfo } from '@/lib/api'

type Props = {
  event: EventInfo
  email: string
  initial: VerificationInfo
  onVerified: (me: Me) => void
  onBack: () => void
}

const CODE_LENGTH = 6

/**
 * New-device resume: the email is already registered for this event and this
 * browser has no session, so the server emailed a one-time code. Verifying
 * resumes the existing run here and signs out the previous device.
 */
export function VerificationForm({ event, email, initial, onVerified, onBack }: Props) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(initial.codeSent ? null : 'A code was sent recently. Check your inbox (and spam).')
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendIn, setResendIn] = useState(initial.resendAvailableInSeconds)

  useEffect(() => {
    if (resendIn <= 0) return
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000)
    return () => clearInterval(id)
  }, [resendIn])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    const trimmed = code.replace(/\D/g, '')
    if (trimmed.length !== CODE_LENGTH) {
      setError(`Enter the ${CODE_LENGTH}-digit code`)
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const me = await api.verify(event.slug, { email, code: trimmed })
      onVerified(me)
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'OTP_EXPIRED' || err.code === 'OTP_LOCKED') setCode('')
        setError(err.message)
      } else {
        setError('Network error. Check your connection and try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResend() {
    if (resending || resendIn > 0) return
    setResending(true)
    setError(null)
    try {
      const { verification } = await api.resendCode(event.slug, email)
      setResendIn(verification.resendAvailableInSeconds)
      setNotice(verification.codeSent ? 'A new code is on its way.' : 'A code was sent recently. Check your inbox (and spam).')
      setCode('')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Network error. Check your connection and try again.')
    } finally {
      setResending(false)
    }
  }

  return (
    <main>
      <h1>{event.name}</h1>
      <form className="card" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <p className="notice" role="status">
          We&apos;ve already seen this email. Enter the verification code we sent you.
        </p>
        <p className="muted">
          Sent to <strong>{email}</strong>. It expires in {Math.round(initial.expiresInSeconds / 60)} minutes. Verifying here
          signs out any other device playing as you.
        </p>

        <div className="field">
          <label htmlFor="otp-code">Verification code</label>
          <input
            id="otp-code"
            className="otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={CODE_LENGTH}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'otp-error' : undefined}
            disabled={submitting}
            autoFocus
          />
          {error && (
            <p id="otp-error" className="field-error" role="alert">
              {error}
            </p>
          )}
          {notice && !error && <p className="muted">{notice}</p>}
        </div>

        <button type="submit" className="primary" disabled={submitting || code.length !== CODE_LENGTH}>
          {submitting ? 'Verifying…' : 'Verify and continue'}
        </button>

        <div className="button-row">
          <button type="button" onClick={() => void handleResend()} disabled={resending || resendIn > 0}>
            {resendIn > 0 ? `Resend code in ${resendIn}s` : resending ? 'Sending…' : 'Resend code'}
          </button>
          <button type="button" onClick={onBack} disabled={submitting}>
            Use a different email
          </button>
        </div>
      </form>
    </main>
  )
}
